if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }

      /* Naturbohne 29.09.2026 (PO): Beim Oeffnen sofort tippen koennen. Der
         Fokus kam bisher erst nach der Einblend-Animation (afterShow). iOS
         oeffnet die Tastatur aber nur, wenn der Fokus noch im selben Tipp
         gesetzt wird. Im Moment des Tipps ist die Schublade aber noch
         unsichtbar (visibility), ein Fokus dort greift nicht (gemessen).
         Deshalb der uebliche Weg: im selben Tipp ein unsichtbares Hilfsfeld
         fokussieren, das oeffnet die Tastatur; sobald die Schublade steht,
         geht der Fokus ans Suchfeld (die Tastatur bleibt offen), das
         Hilfsfeld wird entfernt. */
      show(activeElement = null, animate = true) {
        if (this.open) return super.show(activeElement, animate);
        let hilfe = null;
        try {
          hilfe = document.createElement('input');
          hilfe.type = 'text';
          hilfe.setAttribute('aria-hidden', 'true');
          hilfe.tabIndex = -1;
          hilfe.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;border:0;padding:0;';
          document.body.appendChild(hilfe);
          hilfe.focus({ preventScroll: true });
        } catch (e) {}
        const warten = super.show(activeElement, animate);
        /* Kimi-Befund 4 (30.09.2026): Wird die Schublade waehrend der
           Einblend-Animation wieder geschlossen (Esc), darf der Fokus nicht
           in das versteckte Suchfeld wandern. Dann nur das Hilfsfeld
           entfernen und den Fokus, falls er noch dort sitzt, an den
           Ausloeser zurueckgeben. */
        const weiter = () => {
          const imHilfsfeld = hilfe && document.activeElement === hilfe;
          hilfe?.remove();
          if (!this.open) {
            if (imHilfsfeld && activeElement && typeof activeElement.focus === 'function') {
              try { activeElement.focus({ preventScroll: true }); } catch (e) {}
            }
            return;
          }
          try { this.input?.focus({ preventScroll: true }); } catch (e) {}
        };
        Promise.resolve(warten).then(weiter, weiter);
        return warten;
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        /* Naturbohne 29.09.2026 (Codex-Pruefung T5): laufende Anfrage
           verwerfen, sonst brachte eine spaete Antwort nach dem Leeren
           wieder Treffer. */
        this.abortController?.abort();
        this.removeAttribute('loading');
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        /* Auch hier eine noch laufende aeltere Anfrage verwerfen */
        this.abortController?.abort();
        this.removeAttribute('loading');
        const responseText = this.cachedMap.get(url);
        /* Unbrauchbarer Eintrag (Codex N4-R4): verwerfen und neu holen */
        if (!this.renderSearchResults(responseText)) {
          this.cachedMap.delete(url);
          this.renderSectionFromFetch(url);
          return;
        }

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => { if (!response.ok) throw new Error('HTTP ' + response.status); return response.text(); })
          .then((responseText) => {
            /* Nur Antworten mit Ergebnis-Container cachen (Codex N4-R4: eine
               500er-Fehlerseite lag sonst dauerhaft im Cache und jede
               Wiederholung desselben Begriffs scheiterte bis zum Neuladen). */
            if (!this.ergebnisAus(responseText)) throw new Error('Antwort ohne Ergebnisse');
            this.cachedMap.set(url, responseText);
            /* Nur zeigen, wenn die Antwort noch zur aktuellen Eingabe passt */
            if (this.buildUrl().toString() !== url) return;
            this.renderSearchResults(responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
              if (this.buildUrl().toString() === url) this.removeAttribute('loading');
            }
          });
      }

      ergebnisAus(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        return new DOMParser().parseFromString(responseText || '', 'text/html').getElementById(id);
      }

      /* Gibt false zurueck, wenn die Antwort keinen Ergebnis-Container hat */
      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return true;

        const neu = this.ergebnisAus(responseText);
        if (!neu) return false;
        document.getElementById(id).innerHTML = neu.innerHTML;
        return true;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
